import React, { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, X } from 'lucide-react';

/**
 * Banner que notifica o usuário quando há uma atualização do PWA disponível.
 * Utiliza o hook useRegisterSW do vite-plugin-pwa para gerenciar o ciclo de vida do SW.
 */
export const PWAUpdateBanner: React.FC = () => {
    const {
        offlineReady: [offlineReady, setOfflineReady],
        needRefresh: [needRefresh, setNeedRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        onRegistered(r) {
            console.log('[PWA] SW Registered:', r);
            if (r) {
                // Checar por updates periodicamente (a cada 60s)
                setInterval(() => {
                    console.log('[PWA] Checking for updates...');
                    r.update();
                }, 60 * 1000);
            }
        },
        onRegisterError(error) {
            console.error('[PWA] SW registration error', error);
        },
    });

    // Logging de estado para debug
    useEffect(() => {
        console.log(`[PWA State] OfflineReady: ${offlineReady}, NeedRefresh: ${needRefresh}`);
    }, [offlineReady, needRefresh]);

    const close = () => {
        setOfflineReady(false);
        setNeedRefresh(false);
    };

    // Apenas mostramos o banner se houver uma atualização esperando (needRefresh)
    // offlineReady é apenas informativo ("App pronto para uso offline"), não bloqueante.
    if (!needRefresh) return null;

    return (
        <div className="fixed top-0 left-0 right-0 z-[9999] animate-slide-down">
            <div className="bg-gradient-to-r from-primary to-accent text-white px-4 py-3 shadow-lg border-b border-white/10">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                        <RefreshCw size={20} className="shrink-0 animate-spin-slow" />
                        <div className="flex-1">
                            <p className="font-semibold text-sm">Nova versão disponível!</p>
                            <p className="text-xs text-white/90">Atualize para aplicar as correções recentes.</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                updateServiceWorker(true);
                                // Fallback para Desktop: Forçar o recarregamento caso o plugin não consiga
                                setTimeout(() => window.location.reload(), 500);
                            }}
                            className="bg-white text-primary px-4 py-2 rounded-lg text-sm font-bold shadow-md hover:bg-gray-50 transition-all active:scale-95 whitespace-nowrap"
                        >
                            Atualizar Agora
                        </button>
                        <button
                            onClick={close}
                            className="text-white/80 hover:text-white p-2 transition-colors rounded-full hover:bg-white/10"
                            aria-label="Fechar"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
